import React, { useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import BreadCrumbs from '../common/BreadCrumbs';
import ReusableTabs from '../common/ReusableTabs';
import investorRelationHeaderImage from '../../static/investor_image/investorHeader.png';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import {
    GET_RESOURCE_LIBRARY_NEWS_ITEM_QUERY,
    getResourceLibraryNewsItemQueryVariables,
} from '../../graphql/pimcore/modules/resourceLibrary/queries/getResourceLibraryNewsItemQuery';
import pimcoreClient from '../../graphql/pimcore/apollo/pimcoreClient';
import { transformResourceLibraryNewsItemResponse } from '../../graphql/pimcore/modules/resourceLibrary/tools/transformResourceLibraryNewsItemResponse';
import { ResourceLibraryNewsItem } from '../../graphql/pimcore/modules/resourceLibrary/types/resourceLibraryItems';
import { findLocale, LocaleCode } from '../../utils/Locale';
import DebouncedLoader from '../common/DebouncedLoader';
import { fixPimcoreRootRelativeSrcs, sanitizeHtml } from '../../utils/htmlUtils';
import {
    collectPimcoreAssetIdsFromHtmlBlocks,
    replacePimcoreAssetPlaceholders,
    usePimcoreAssets,
} from '../../utils/pimcoreAssetPlaceholders';
import { toUrl } from '@/utils/convertImagePath';

interface NewsResourcePageProps {
    id: string;
    locale: string;
}

const NewsResourcePage: React.FC<NewsResourcePageProps> = ({ id, locale }) => {
    const localeCode: LocaleCode = findLocale(locale).locale;

    // Query to get the news item
    const {
        loading: isItemLoading,
        error: itemError,
        data: itemData,
    } = useQuery(GET_RESOURCE_LIBRARY_NEWS_ITEM_QUERY, {
        variables: getResourceLibraryNewsItemQueryVariables(localeCode, Number(id)),
        client: pimcoreClient,
    });

    useEffect(() => {
        if (itemError) {
            console.error('GraphQL News Query Error:', itemError);
        }
    }, [itemError]);

    // Transform the raw query data into a news item
    const newsItem = useMemo<ResourceLibraryNewsItem | null>(() => {
        if (!itemData) return null;
        const item = transformResourceLibraryNewsItemResponse(itemData);
        if (!item || !item.body) {
            return item;
        }
        return {
            ...item,
            body: fixPimcoreRootRelativeSrcs(item.body, true),
        };
    }, [itemData]);

    const assetIdsFromBody = useMemo<string[]>(() => {
        if (!newsItem || !newsItem.body) {
            return [];
        }

        return collectPimcoreAssetIdsFromHtmlBlocks([newsItem.body]);
    }, [newsItem]);

    const {
        assetsById,
        loading: isAssetsLoading,
        error: assetsError,
    } = usePimcoreAssets(assetIdsFromBody, { client: pimcoreClient });

    const newsItemWithAssets = useMemo<ResourceLibraryNewsItem | null>(() => {
        if (!newsItem) {
            return null;
        }

        if (assetsById.size === 0 || !newsItem.body) {
            return newsItem;
        }

        const body = replacePimcoreAssetPlaceholders(newsItem.body, assetsById);
        return { ...newsItem, body };
    }, [newsItem, assetsById]);

    const isLoading = isItemLoading || isAssetsLoading;

    if (!isLoading) {
        if (itemError) return <div className="mid-width">Error: {itemError.message}</div>;
        if (assetsError) {
            const message =
                assetsError instanceof Error ? assetsError.message : 'Failed to load assets.';
            return <div className="mid-width">Error: {message}</div>;
        }
        if (!newsItem) return <div className="mid-width">News item not found.</div>;
    }

    let sanitizedBody = sanitizeHtml(newsItemWithAssets?.body || '');

    return (
        <DebouncedLoader loading={isLoading} delay={1000} size={40} centered={true}>
            <ReusableTabs
                isHtmlAsset={true}
                backgroundColor="#ffffff"
                bannerImage={toUrl(investorRelationHeaderImage)}
                bannerTitle={newsItem?.name || ''}
                bannerDescription=""
                tabLabels={[]}
                tabKeys={['']}
                tabContents={[
                    <div className="mid-width" key="news">
                        <div className="html-pages-wrapper">
                            <div dangerouslySetInnerHTML={{ __html: sanitizedBody }} />
                        </div>
                    </div>,
                ]}
                bannerFontStyle={styles.bannerFont}
                subBannerFontStyle={styles.bannerFont2}
                pageName="browseAllProductHome"
                showTabs={false}
                breadcrumbs={
                    <BreadCrumbs navigationPath="/Home/SalesAndResources/ResourceLibrary" />
                }
            />
        </DebouncedLoader>
    );
};

export default NewsResourcePage;
